import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import DataTable from "react-data-table-component";
import { API_BASE_URL } from "../../config";
import { ToastContainer, toast } from "react-toastify";

const TicketView = () => {
  const { id } = useParams(); // Ticket ID from URL

  /* ---------------- STATES ---------------- */
  const [ticket, setTicket] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);

  const [form, setForm] = useState({
    work_title: "",
    work_start_date: "",
    work_end_date: "",
    work_cost: "",
    work_summary: "",
    work_doc: null,
  });

  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  /* ---------------- AUTH HEADER ---------------- */
  const getAuthHeader = () => {
    const token = localStorage.getItem("authToken");
    return { Authorization: `Bearer ${token}` };
  };

  /* ---------------- LOAD TICKET INFO ---------------- */
  const loadTicketInfo = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/tickets/${id}/details`,
        { headers: getAuthHeader() }
      );
      setTicket(res.data.ticket);
    } catch (err) {
      console.error("Ticket load error:", err);
    }
  };

  /* ---------------- LOAD WORK ORDERS ---------------- */
 const loadWorkOrders = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/tickets/${id}/workorder`, {
      headers: getAuthHeader(),
    });

    const data = Array.isArray(res.data) ? res.data : [];

   setWorkOrders(Array.isArray(res.data) ? res.data : []);

  } catch (err) {
    console.error("Work order load error:", err);
    setWorkOrders([]);
  }
};


  useEffect(() => {
    loadTicketInfo();
    loadWorkOrders();
  }, [id]);


  /* ---------------- FORM HANDLERS ---------------- */
  const handleInput = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFile = (e) => {
    setForm({ ...form, work_doc: e.target.files[0] });
  };

  /* ---------------- SUBMIT WORK ORDER ---------------- */
  const submitWorkOrder = async (e) => {
  e.preventDefault();

  setSaving(true);

  const data = new FormData();
  data.append("asset_id", ticket.asset_id);
  data.append("work_title", form.work_title);
  data.append("work_start_date", form.work_start_date);
  data.append("work_end_date", form.work_end_date);
  data.append("work_cost", form.work_cost);
  data.append("work_summary", form.work_summary);
  if (form.work_doc) data.append("work_doc", form.work_doc);

  try {
    await axios.post(
      `${API_BASE_URL}/tickets/${id}/workorder`,
      data,
      {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "multipart/form-data",
        },
      }
    );

    toast.success("Work Order Added");

    // reset form
    setForm({
      work_title: "",
      work_start_date: "",
      work_end_date: "",
      work_cost: "",
      work_summary: "",
      work_doc: null,
    });

    loadWorkOrders();

  } catch (err) {
    console.error(err);

    // ✅ Catch Laravel validation errors (HTTP 422)
    if (err.response && err.response.status === 422) {
      const errors = err.response.data.errors;

      Object.values(errors).forEach((msgs) => {
        toast.error(msgs[0]); // show each validation error
      });

      return;
    }

    toast.error("Failed to add work order");

  } finally {
    setSaving(false);
  }
};


  /* ---------------- FILTER WORK ORDERS ---------------- */
  const filteredWorkOrders = workOrders.filter((row) =>
    Object.values(row).some((v) =>
      String(v).toLowerCase().includes(search.toLowerCase())
    )
  );

  /* ---------------- DATATABLE COLUMNS ---------------- */
  const columns = [
    { name: "Asset Code", selector: (row) => row.asset_code, sortable: true },
    { name: "Asset Name", selector: (row) => row.asset_name, sortable: true },
    { name: "Order Title", selector: (row) => row.order_title, sortable: true },
    { name: "Cost", selector: (row) => row.work_cost, sortable: true },
    { name: "Date", selector: (row) => row.created_on, sortable: true },
    {
      name: "Document",
      cell: (row) =>
        row.work_doc ? (
          <a
            href={row.document_url}
            target="_blank"
            rel="noreferrer"
            className="text-primary"
          >
            document
          </a>
        ) : (
          "-"
        ),
    },
  ];

  if (!ticket) return <div>Loading...</div>;

  return (
    <div className="container mt-4">
     <ToastContainer position="bottom-right" />

      <h4 className="mb-3">
        Work Orders — Ticket: {ticket.ticket_code}
      </h4>

      {/* ---------------- WORK ORDER FORM ---------------- */}
      <div className="card p-3 mb-3">
        <h5>Add Work Order</h5>

        <form onSubmit={submitWorkOrder}>
          <div className="row g-3">

            <div className="col-md-12 required-tooltip">
              <label>Work Title</label>
              <input
                type="text"
                name="work_title"
                className="form-control required-input"
                value={form.work_title}
                onChange={handleInput}
              />
            </div>

            <div className="col-md-3">
              <label>Start Date</label>
              <input
                type="date"
                name="work_start_date"
                className="form-control required-input"
                value={form.work_start_date}
                onChange={handleInput}
              />
            </div>

            <div className="col-md-3">
              <label>End Date</label>
              <input
                type="date"
                name="work_end_date"
                className="form-control required-input"
                value={form.work_end_date}
                onChange={handleInput}
              />
            </div>

            <div className="col-md-3">
              <label>Cost</label>
              <input
                type="number"
                name="work_cost"
                className="form-control required-input" 
                value={form.work_cost}
                onChange={handleInput}
              />
            </div>

            <div className="col-md-12">
              <label>Upload Document</label>
              <input
                type="file"
                className="form-control required-input"
                onChange={handleFile}
              />
            </div>

            <div className="col-md-12">
              <label>Work Summary</label>
              <textarea
                name="work_summary"
                className="form-control required-input"
                rows="3"
                value={form.work_summary}
                onChange={handleInput}
              ></textarea>
            </div>
<div className="col-md-12 text-end mt-3">
  <button
    className="btn btn-danger px-4 btn-height"
    disabled={saving}
  >
    {saving ? "Saving..." : "Save Work Order"}
  </button>
</div>


          </div>
        </form>
      </div>

      {/* ---------------- WORK ORDER LIST ---------------- */}
      <div className="card p-3">

        <h5 className="mb-3">Work Order List</h5>

        <div className="row mb-2">
          <div className="col-md-4">
            <input
              type="text"
              placeholder="Search..."
              className="form-control"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <DataTable
          columns={columns}
          data={filteredWorkOrders}
          pagination
          highlightOnHover
          striped
          responsive
        />
      </div>
    </div>
  );
};

export default TicketView;
